import { inject, Injectable } from '@angular/core';
import {
    CanvasInteractionEvent,
    DriverscamVideoMinimumInfo,
    Hotspot,
    IDisplaySlide,
    MinimumSlideInformation,
    QuestionIdentifier,
} from '@pcp/core/interfaces';
import { LessonHubFacade } from '@pcp/streaming';

/** Layer index in the slide-preview component. 1 = primary, 2 = secondary, 3 = tertiary. */
type Layer = 1 | 2 | 3;

/**
 * LessonCommandFacade — the public API for ephemeral SignalR commands on the
 * lesson hub. Sibling to `ClassroomFacade` (which owns persistent Y.Doc
 * state); both are classroom-owned and share the same `LessonHubFacade`
 * transport, lesson scope, and lifecycle.
 *
 * The split is justified by SEMANTICS, not client count:
 *  - `ClassroomFacade` answers *what is the lesson?* (persistent, CRDT-synced,
 *    writer-only via `guardWrite`).
 *  - `LessonCommandFacade` answers *what is happening right now?* (ephemeral,
 *    not persisted, replay-skipped for late joiners, any sender).
 *
 * Two channels exposed (corresponding to the three-channel model in the
 * write-path-architecture synthesis):
 *
 *  - `broadcast.*` — **Channel 2** transient broadcasts: ephemeral events
 *    that do NOT modify lesson state and are broadcast to all clients in
 *    the lesson group (typically initiated by the writer client, e.g.,
 *    video lifecycle, perception reveal, drawing rubber-element).
 *
 *  - `sendIntent.*` — **Channel 3** intent forwarding: non-writer clients
 *    (beamer, student) request the writer to perform a state-change they
 *    cannot perform themselves. Writer-side effects handle the inbound
 *    intent and decide what state to write.
 *
 * Each method is a thin typed wrapper over a `LessonHubFacade` send method.
 * Stage B introduces the facade as an additive surface; Stage D migrates
 * caller-side dispatch sites onto it (replacing `LessonHubFacade.send*`
 * calls in effects).
 */
@Injectable({ providedIn: 'root' })
export class LessonCommandFacade {
    private readonly hub = inject(LessonHubFacade);

    // -----------------------------------------------------------------------
    // Channel 2 — transient broadcasts (ephemeral; sender-to-all-receivers)
    // -----------------------------------------------------------------------

    readonly broadcast = {
        videoStartedPlaying: (
            layer: Layer,
            slide: IDisplaySlide,
            playbackStartPosition: number,
        ): Promise<void> =>
            this.hub.sendVideoStartedPlaying(slide, playbackStartPosition, layer),

        videoPaused: (layer: Layer, slide: IDisplaySlide): Promise<void> =>
            this.hub.sendVideoPaused(slide, layer),

        videoLoaded: (layer: Layer, slide: IDisplaySlide): Promise<void> =>
            this.hub.sendVideoLoaded(slide, layer),

        videoSeeked: (
            layer: Layer,
            slide: IDisplaySlide,
            seekedToPosition: number,
            isInPausedState: boolean,
        ): Promise<void> =>
            this.hub.sendVideoSeeked(slide, seekedToPosition, isInPausedState, layer),

        /**
         * HTML "rubber element" mutation (drawings, eraser strokes) broadcast
         * to other clients. Distinct from `ClassroomFacade.appendMutationBatch`
         * which is the Y.Doc-backed mutation path; this is the SignalR-direct
         * pulse for live drawing collaboration.
         */
        rubberElementMutation: (
            layer: Layer,
            mutation: CanvasInteractionEvent,
            slide: IDisplaySlide,
        ): Promise<void> =>
            this.hub.updateRubberElementContent(mutation, slide, layer),
    } as const;

    // -----------------------------------------------------------------------
    // Channel 3 — intent forwarding (non-writer → writer; one specific receiver)
    // -----------------------------------------------------------------------

    readonly sendIntent = {
        /**
         * Beamer/student asks the teacher to navigate to a thumbnail in the
         * next-slides preview area.
         */
        navigateToPreviewSlide: (slide: MinimumSlideInformation): Promise<void> =>
            this.hub.sendNavigateToPreviewSlideToTeacher(slide),

        /**
         * Beamer/student asks the teacher to show a driverscam assignment
         * from the next-slides preview area.
         */
        navigateToPreviewDriverscam: (driverscam: DriverscamVideoMinimumInfo): Promise<void> =>
            this.hub.sendNavigateToPreviewDriverscamAssignmentToTeacher(driverscam),

        /**
         * Beamer/student asks the teacher to show a difficult-question element
         * for the supplied question numbers.
         */
        showDifficultQuestionElement: (numbers: QuestionIdentifier[]): Promise<void> =>
            this.hub.sendShowDifficultQuestionElementToTeacher(numbers),

        /**
         * Beamer-side hotspot click: asks the teacher to navigate to the
         * hotspot's target slide. (Teacher-side hotspot pulse — the visual
         * pulse on other clients when the writer clicks — is currently not
         * a separate broadcast; the navigation IS the cross-client event.)
         */
        activateHotspot: (hotspot: Hotspot, slide: IDisplaySlide, layer: Layer = 1): Promise<void> =>
            this.hub.sendActivateHotspotToTeacher(hotspot, slide, layer),

        /** Beamer toolbar undo → forward to teacher. */
        undo: (): Promise<void> => this.hub.sendUndoCommandToTeacher(),

        /** Beamer toolbar redo → forward to teacher. */
        redo: (): Promise<void> => this.hub.sendRedoCommandToTeacher(),

        /** Beamer toolbar navigate-to-parent → forward to teacher. */
        navigateToParent: (): Promise<void> => this.hub.sendNavigateToParentCommandToTeacher(),
    } as const;
}
